/**
 * Create Blog Post Mutation Hook
 *
 * Create new blog posts with automatic cache invalidation.
 * Computes word_count and reading_time automatically.
 *
 * @example
 * const { mutate, isPending } = useCreateBlogPost();
 * mutate(formData, { onSuccess: (post) => console.log('Created:', post.id) });
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createBlogPost } from '../api';
import { blogPostsKeys } from './useBlogPosts';
import type { BlogPost, BlogPostFormData } from '../types';
import { stripHtml, calculateWordCount, calculateReadingTime } from '../utils/contentStats';

/**
 * Hook to create a new blog post
 * Automatically computes word_count and reading_time from content
 *
 * @returns Mutation object with mutate function and state
 */
export const useCreateBlogPost = () => {
  const queryClient = useQueryClient();

  return useMutation<BlogPost, Error, BlogPostFormData>({
    mutationFn: async (data: BlogPostFormData) => {
      const plainText = stripHtml(data.content);
      const wordCount = calculateWordCount(plainText);

      const blogData: Partial<BlogPost> = {
        ...data,
        word_count: wordCount,
        reading_time: calculateReadingTime(wordCount),
      };

      return createBlogPost(blogData);
    },
    onSuccess: () => {
      // Invalidate blog posts list to refetch
      void queryClient.invalidateQueries({ queryKey: blogPostsKeys.all });
    },
    onError: (error) => {
      console.error('Error creating blog post:', error);
    },
  });
};
